/**
 * Typed token exports for JS consumers (annotation canvas, charts, etc.).
 * Keep in sync with tokens.css.
 */

export const spacing = {
  sp1: 4, sp2: 8, sp3: 12, sp4: 16, sp5: 20, sp6: 24,
  sp8: 32, sp10: 40, sp12: 48, sp16: 64, sp20: 80,
} as const;

export const radii = {
  sm: 4, md: 6, lg: 8, xl: 12, '2xl': 16, full: 9999,
} as const;

export const typography = {
  fs: { xs: 11, sm: 12, base: 13, md: 14, lg: 16, xl: 20, '2xl': 24, '3xl': 32, '4xl': 40 },
  lh: { tight: 1.25, normal: 1.5, relaxed: 1.65 },
  fw: { regular: 400, medium: 500, semibold: 600, bold: 700 },
} as const;

export const motion = {
  durFast: 120, dur: 180, durSlow: 280,
  ease: 'cubic-bezier(0.2, 0, 0, 1)',
} as const;

/**
 * Highlight palette hex values — used for the annotation color picker and
 * anywhere else JS needs the actual color value (not just the CSS variable).
 */
export const highlights = {
  blue:    '#3B82F6',
  violet:  '#8B5CF6',
  magenta: '#EC4899',
  green:   '#10B981',
  amber:   '#F59E0B',
  red:     '#EF4444',
  cyan:    '#06B6D4',
} as const;

export type HighlightColor = keyof typeof highlights;

/** The six colors exposed in the annotation palette, in display order. */
export const annotationColors: HighlightColor[] = ['red', 'amber', 'green', 'blue', 'violet', 'magenta'];

/**
 * The 12-color palette used for project dots in the sidebar, board cards,
 * and the extension popup's project dropdown. Ordered as a rainbow so
 * `nextUnusedProjectColor` produces a visually pleasant rotation when
 * a workspace creates several projects in a row.
 *
 * Users can override with any custom hex via the project settings UI;
 * this list is the default-assignment pool, not a hard restriction.
 */
export const PROJECT_COLORS = [
  '#3B82F6', // blue
  '#06B6D4', // cyan
  '#14B8A6', // teal
  '#10B981', // green
  '#84CC16', // lime
  '#F59E0B', // amber
  '#F97316', // orange
  '#EF4444', // red
  '#EC4899', // pink
  '#A855F7', // violet
  '#8B5CF6', // purple
  '#6366F1', // indigo
] as const;

/**
 * Pick the first palette color not already in use by any of the existing
 * project colors. Wraps to a deterministic position if all 12 are taken so
 * a 13th project doesn't get stuck on `palette[0]` repeatedly.
 *
 * Case-insensitive comparison so `#3b82f6` and `#3B82F6` count as the same
 * color — Postgres + the form input might disagree on case.
 */
export function nextUnusedProjectColor(existingColors: readonly string[]): string {
  const used = new Set(existingColors.map((c) => c.toLowerCase()));
  for (const color of PROJECT_COLORS) {
    if (!used.has(color.toLowerCase())) return color;
  }
  return PROJECT_COLORS[existingColors.length % PROJECT_COLORS.length]!;
}

export const zIndex = {
  sticky: 50, dropdown: 100, modal: 200, toast: 300, tooltip: 400,
} as const;
